import type { ReactNode } from "react";
import { Avatar, IconButton, SidebarNav } from "../components";
import { Icon, useLucideIcons } from "../components/Icon";
import { useIsMobile } from "../lib/hooks";
import logoMark from "../assets/logo-mark.svg";

export type AdminPage = "dashboard" | "schedule" | "people" | "leave" | "reports" | "settings";

export interface ShellProps {
  page: AdminPage;
  onNav: (p: AdminPage) => void;
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  pendingLeaves?: number;
  onLogout?: () => void;
}

const BOTTOM_NAV: { value: AdminPage; label: string; icon: string }[] = [
  { value: "dashboard", label: "概览",   icon: "layout-dashboard" },
  { value: "schedule",  label: "排课",   icon: "calendar-days" },
  { value: "people",    label: "学员",   icon: "users" },
  { value: "reports",   label: "报表",   icon: "file-bar-chart" },
  { value: "settings",  label: "设置",   icon: "settings" },
];

export function Shell({ page, onNav, title, subtitle, actions, children, pendingLeaves = 0, onLogout }: ShellProps) {
  const isMobile = useIsMobile();
  useLucideIcons([page, isMobile]);

  /* ── Mobile layout ────────────────────────────────────────────────── */
  if (isMobile) {
    return (
      <div style={{ display: "flex", flexDirection: "column", height: "100dvh", background: "var(--surface-page)" }}>
        {/* Top header */}
        <header
          style={{
            display: "flex", alignItems: "center", gap: 10, padding: "10px 14px",
            background: "var(--surface-card)", borderBottom: "1px solid var(--border-subtle)",
            position: "sticky", top: 0, zIndex: 50, flex: "none",
          }}
        >
          <img src={logoMark} width={26} height={26} alt="记时" />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "var(--text-strong)", lineHeight: 1.2 }}>
              {title}
            </div>
            {subtitle && (
              <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {subtitle}
              </div>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flex: "none" }}>
            {actions}
            {onLogout && (
              <IconButton label="退出" size="sm" onClick={onLogout} style={{ color: "var(--text-subtle)" }}>
                <Icon name="log-out" size={16} />
              </IconButton>
            )}
          </div>
        </header>

        {/* Page content */}
        <main style={{ flex: 1, overflow: "auto", padding: "14px 14px 72px" }}>
          {children}
        </main>

        {/* Bottom navigation */}
        <nav
          style={{
            position: "fixed", bottom: 0, left: 0, right: 0,
            background: "var(--surface-card)", borderTop: "1px solid var(--border-subtle)",
            display: "flex", zIndex: 100,
            paddingBottom: "env(safe-area-inset-bottom, 0px)",
          }}
        >
          {BOTTOM_NAV.map((item) => {
            const isActive = page === item.value;
            const badge = item.value === "leave" && pendingLeaves > 0 ? pendingLeaves : 0;
            return (
              <button
                key={item.value}
                type="button"
                onClick={() => onNav(item.value)}
                style={{
                  flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
                  justifyContent: "center", gap: 3, padding: "8px 2px 10px",
                  border: "none", background: "transparent", cursor: "pointer",
                  color: isActive ? "var(--brand)" : "var(--text-subtle)",
                  position: "relative", WebkitTapHighlightColor: "transparent",
                }}
              >
                <Icon name={item.icon} size={22} />
                <span style={{ fontSize: 10, fontWeight: isActive ? 700 : 400, lineHeight: 1 }}>
                  {item.label}
                </span>
                {badge > 0 && (
                  <span
                    style={{
                      position: "absolute", top: 6, left: "calc(50% + 6px)",
                      background: "var(--danger-fg)", color: "#fff", borderRadius: 99,
                      fontSize: 9, fontWeight: 700, minWidth: 14, height: 14,
                      display: "flex", alignItems: "center", justifyContent: "center", padding: "0 3px",
                    }}
                  >
                    {badge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>
    );
  }

  /* ── Desktop layout (unchanged) ───────────────────────────────────── */
  const sideItems = [
    { value: "dashboard", label: "概览",     icon: <Icon name="layout-dashboard" size={20} /> },
    { value: "schedule",  label: "排课日历", icon: <Icon name="calendar-days" size={20} /> },
    { group: "管理" },
    { value: "people",    label: "人员管理", icon: <Icon name="users" size={20} /> },
    {
      value: "leave",
      label: "请假审批",
      icon: <Icon name="calendar-x" size={20} />,
      badge: pendingLeaves > 0 ? pendingLeaves : undefined,
    },
    { value: "reports",   label: "课时报表", icon: <Icon name="file-bar-chart" size={20} /> },
    { group: "系统" },
    { value: "settings",  label: "设置",     icon: <Icon name="settings" size={20} /> },
  ];

  return (
    <div style={{ display: "flex", height: "100vh", background: "var(--surface-page)" }}>
      <aside
        style={{
          width: "var(--sidebar-w)", flex: "none",
          background: "var(--surface-card)", borderRight: "1px solid var(--border-subtle)",
          display: "flex", flexDirection: "column", padding: "18px 14px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "4px 8px 18px" }}>
          <img src={logoMark} width={34} height={34} alt="记时" />
          <span style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 20, color: "var(--text-strong)" }}>
            记时{" "}
            <span style={{ color: "var(--brand)", fontSize: 11, letterSpacing: 2 }}>JISHI</span>
          </span>
        </div>
        <SidebarNav value={page} onChange={(v) => onNav(v as AdminPage)} items={sideItems} />
        <div
          style={{
            marginTop: "auto", display: "flex", alignItems: "center", gap: 10,
            padding: "10px 8px", borderTop: "1px solid var(--divider)",
          }}
        >
          <Avatar name="校务老师" size="sm" />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-strong)" }}>校务老师</div>
            <div style={{ fontSize: 11, color: "var(--text-subtle)" }}>WELL-E · 管理员</div>
          </div>
          {onLogout && (
            <IconButton label="退出登录" size="sm" onClick={onLogout} style={{ color: "var(--text-subtle)" }}>
              <Icon name="log-out" size={16} />
            </IconButton>
          )}
        </div>
      </aside>

      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <header
          style={{
            display: "flex", alignItems: "center", gap: 16, padding: "16px 28px",
            background: "color-mix(in srgb, var(--surface-card) 80%, transparent)",
            backdropFilter: "blur(8px)", borderBottom: "1px solid var(--border-subtle)",
            position: "sticky", top: 0, zIndex: 5,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={{ fontSize: "var(--text-h1)", fontWeight: 700, color: "var(--text-strong)" }}>{title}</h1>
            {subtitle && <p style={{ fontSize: "var(--text-sm)", color: "var(--text-muted)", marginTop: 2 }}>{subtitle}</p>}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>{actions}</div>
        </header>
        <main style={{ flex: 1, overflow: "auto", padding: "24px 28px 40px" }}>{children}</main>
      </div>
    </div>
  );
}
